// useDictation — browser-native voice → text hook (Web Speech API).
// Call with a [text, setText] pair; returns controls to start/stop dictation
// and a flag for whether speech is supported. Final words append; interim
// words preview live. Same pattern as MeditationsView, extracted for reuse.
//
// Usage:
//   const { recording, interim, speechSupported, start, stop, error } =
//     useDictation(text, setText);

import { useEffect, useRef, useState } from "react";

export function useDictation(text, setText) {
  const [recording, setRecording] = useState(false);
  const [interim, setInterim]     = useState("");
  const [error,    setError]      = useState(null);
  const recogRef      = useRef(null);
  const baseTextRef   = useRef("");
  const finalChunkRef = useRef("");

  const speechSupported = typeof window !== "undefined" &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = () => {
    if (recording || !speechSupported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = "en-US";
    r.continuous = true;
    r.interimResults = true;

    baseTextRef.current = text ? text.replace(/\s+$/, "") + (text.trim() ? " " : "") : "";
    finalChunkRef.current = "";
    setInterim("");
    setError(null);

    r.onresult = (ev) => {
      let interimChunk = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const transcript = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) {
          finalChunkRef.current += (finalChunkRef.current ? " " : "") + transcript.trim();
        } else {
          interimChunk += transcript;
        }
      }
      setText(baseTextRef.current + finalChunkRef.current + (interimChunk ? (finalChunkRef.current ? " " : "") + interimChunk : ""));
      setInterim(interimChunk);
    };
    r.onerror = (ev) => {
      console.warn("SpeechRecognition error", ev.error);
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setError("Microphone permission denied. Allow mic access in browser settings.");
      } else if (ev.error !== "no-speech") {
        setError("Dictation error: " + ev.error);
      }
    };
    r.onend = () => {
      setRecording(false);
      setInterim("");
      setText(baseTextRef.current + finalChunkRef.current);
    };

    try {
      r.start();
      recogRef.current = r;
      setRecording(true);
    } catch (e) {
      setError("Couldn't start dictation: " + e.message);
    }
  };

  const stop = () => {
    try { recogRef.current?.stop(); } catch {}
  };

  useEffect(() => {
    return () => { try { recogRef.current?.stop(); } catch {} };
  }, []);

  return { recording, interim, speechSupported, start, stop, error };
}

// Reusable dictation button — small pill styled for dark CRM / staff UI.
// Pass the hook's state + start/stop. Optional color overrides.
export function DictateButton({ recording, interim, speechSupported, onStart, onStop, error, disabled, style }) {
  if (!speechSupported) {
    return (
      <span style={{ color: "rgba(255,218,173,0.45)", fontSize: "0.72rem", alignSelf: "center" }}>
        🎙 Dictation not supported (try Chrome or Safari)
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={recording ? onStop : onStart}
        disabled={disabled}
        style={{
          display: "inline-flex", alignItems: "center", gap: "0.4rem",
          background: recording ? "rgba(239,83,126,0.16)" : "rgba(74,164,225,0.14)",
          color: recording ? "#ef537e" : "#4aa4e1",
          border: `1px solid ${recording ? "#ef537e" : "#4aa4e1"}`,
          borderRadius: "999px",
          padding: "0.38rem 0.85rem",
          fontSize: "0.82rem",
          fontWeight: 700,
          fontFamily: "inherit",
          cursor: disabled ? "not-allowed" : "pointer",
          ...style,
        }}
      >
        <span style={{
          width: "8px", height: "8px", borderRadius: "50%",
          background: recording ? "#ef537e" : "transparent",
          border: recording ? "none" : "1px solid #4aa4e1",
          animation: recording ? "pulse 1.2s ease-in-out infinite" : "none",
        }} />
        {recording ? (interim ? "listening…" : "Stop") : "🎙 Dictate"}
      </button>
      {error && <span style={{ color: "#ff8a8a", fontSize: "0.75rem", marginLeft: "0.6rem" }}>{error}</span>}
    </>
  );
}
